// DS.7 (sprint aos-ds-settings-modals, D17) — the app's ONE confirm surface,
// now on `@ds/overlays/Modal` through `ds/DsModal`.
//
// `ConfirmDialogProps` is UNCHANGED: five call sites, nine usages, and three
// of the five hosts (`ChatHeader` ×4, `Sidebar`, `organisms/Composer`) belong
// to DS.5 and DS.6 and are behind this sprint's fence — so the blast radius of
// the whole app's confirm flow is reached through this one file and no host is
// edited `[medido at open: 5 import sites under web/src, 2026-09-14]`.
//
// NOT `@ds/feedback/Dialog`, and the reason is a fence, not taste: this
// component guards `onOpenChange={(v) => { if (!v && !loading) onCancel(); }}`
// — a dismiss-lock while the confirm action is in flight. `feedback/Dialog`
// calls `onClose` unconditionally from the backdrop and has no `loading`
// concept (Dialog.tsx:116-150), so the obvious mapping would silently delete
// the lock. `Modal` has `closeOnBackdrop`, so the fence survives as
// `closeOnBackdrop={!loading}`.
//
// `role="alertdialog"` is passed as a prop: the piece hardcodes
// `role="dialog"` but spreads `{...props}` LAST (Modal.tsx:121-128), so the
// prop wins. Destructive confirms therefore announce as what they are.
//
// `showCloseButton={false}` — a confirm has two answers and a ✕ is a third,
// ambiguous one.

import { useTranslation } from '../i18n';
import { DsModal } from './ds/DsModal';
import Button from '@ds/actions/Button';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive = false,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('confirm.confirm');
  const resolvedCancelLabel = cancelLabel ?? t('confirm.cancel');

  return (
    <DsModal
      open={open}
      onClose={() => { if (!loading) onCancel(); }}
      size="sm"
      title={title}
      role="alertdialog"
      showCloseButton={false}
      closeOnBackdrop={!loading}
      data-testid="confirm-dialog"
      footer={
        <div className="flex flex-row justify-end gap-2 w-full">
          <Button
            variant="tertiary"
            onClick={onCancel}
            disabled={loading}
            data-testid="confirm-dialog-cancel"
          >
            {resolvedCancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'filled'}
            onClick={onConfirm}
            disabled={loading}
            data-testid="confirm-dialog-confirm"
          >
            {loading ? t('confirm.working') : resolvedConfirmLabel}
          </Button>
        </div>
      }
    >
      {body && <p className="text-body text-muted-foreground">{body}</p>}
    </DsModal>
  );
}
